import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bot, GripVertical, Link2, Loader2, MessageCircle, Trash2, Video } from 'lucide-react';
import { formatDate, formatTime } from '../../../utils/dateUtils';

/** Build AskAgentModal preset from a board card’s linked booking / entity. */
export const agentPresetFromWorkspaceItem = (item) => {
  if (!item) return null;
  if (item.linked_booking_id) {
    return {
      type: 'booking',
      id: String(item.linked_booking_id),
      label: item.link_label || item.title || 'Booking',
    };
  }
  if (item.linked_entity_type && item.linked_entity_id) {
    return {
      type: String(item.linked_entity_type),
      id: String(item.linked_entity_id),
      label: item.link_label || item.title || 'Linked record',
    };
  }
  return null;
};

export const WORKSPACE_COLUMNS = [
  { status: 'open', label: 'To do', short: 'To do', hint: 'Backlog' },
  { status: 'in_progress', label: 'Doing', short: 'Doing', hint: 'Active' },
  { status: 'done', label: 'Done', short: 'Done', hint: 'Finished' },
];

const typeAccent = (type, status) => {
  if (status === 'done') return 'bg-surface border border-border text-ink-secondary';
  if (type === 'meeting') return 'bg-ink text-ink-on-dark';
  if (type === 'schedule') return 'bg-ink-secondary text-ink-on-dark';
  if (type === 'reminder') return 'bg-surface border border-border-strong text-ink';
  return 'bg-ink text-ink-on-dark';
};

function WorkspaceCard({
  item,
  dragging = false,
  onDelete,
  onWhatsApp,
  onRunAgent,
  canWhatsapp = false,
  canAgents = false,
}) {
  const done = item.status === 'done';
  const accent = typeAccent(item.type, item.status);
  const agentPreset = agentPresetFromWorkspaceItem(item);

  return (
    <div
      className={`group relative rounded-sm px-2.5 py-2 ${accent} ${
        dragging ? 'ring-2 ring-ink/30' : ''
      }`}
    >
      <div className="mb-0.5 flex items-start gap-1.5">
        <GripVertical className="mt-0.5 h-3 w-3 shrink-0 opacity-50" />
        <p
          className={`min-w-0 flex-1 text-[12px] font-semibold leading-snug ${
            done ? 'line-through opacity-70' : ''
          }`}
        >
          {item.title}
        </p>
        {onRunAgent && agentPreset ? (
          <button
            type="button"
            className="rounded p-0.5 opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onRunAgent(item, agentPreset);
            }}
            title={canAgents ? 'Run agent on linked record' : 'Agents (Pro)'}
          >
            <Bot className="h-3 w-3" />
          </button>
        ) : null}
        {onWhatsApp ? (
          <button
            type="button"
            className="rounded p-0.5 opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onWhatsApp(item);
            }}
            title={canWhatsapp ? 'WhatsApp draft' : 'WhatsApp (Pro)'}
          >
            <MessageCircle className="h-3 w-3" />
          </button>
        ) : null}
        {onDelete ? (
          <button
            type="button"
            className="rounded p-0.5 opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            title="Delete"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 pl-4 text-[10px] opacity-75">
        <span className="uppercase tracking-wide">{item.type}</span>
        {item.priority && item.priority !== 'normal' ? (
          <span className="uppercase tracking-wide">{item.priority}</span>
        ) : null}
        {item.due_at ? (
          <span>
            {formatDate(item.due_at)} {formatTime(item.due_at)}
          </span>
        ) : null}
        {item.starts_at ? (
          <span>
            {formatDate(item.starts_at)} {formatTime(item.starts_at)}
          </span>
        ) : null}
      </div>
      {(item.meet_link || item.link_href) && (
        <div className="mt-1 flex flex-wrap gap-2 pl-4 text-[10px]">
          {item.meet_link ? (
            <a
              href={item.meet_link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 underline-offset-2 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Video className="h-2.5 w-2.5" />
              Meet
            </a>
          ) : null}
          {item.link_href ? (
            <Link
              to={item.link_href}
              className="inline-flex items-center gap-0.5 underline-offset-2 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <Link2 className="h-2.5 w-2.5" />
              {item.link_label || 'Linked'}
            </Link>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SortableCard({ item, onDelete, onWhatsApp, onRunAgent, canWhatsapp, canAgents }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { item, status: item.status },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`touch-manipulation ${isDragging ? 'z-10 opacity-40' : ''} cursor-grab active:cursor-grabbing`}
      {...attributes}
      {...listeners}
    >
      <WorkspaceCard
        item={item}
        dragging={isDragging}
        onDelete={onDelete}
        onWhatsApp={onWhatsApp}
        onRunAgent={onRunAgent}
        canWhatsapp={canWhatsapp}
        canAgents={canAgents}
      />
    </div>
  );
}

function BoardColumn({ column, children, count, isOver, dimmed }) {
  const { setNodeRef } = useDroppable({ id: `col-${column.status}` });

  return (
    <div
      ref={setNodeRef}
      className={`flex min-h-[480px] flex-col border-r border-border last:border-r-0 ${
        dimmed ? 'opacity-40' : ''
      } ${isOver ? 'bg-surface' : 'bg-canvas'}`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5 sm:px-4">
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-semibold tabular-nums text-ink leading-none">
            {String(count).padStart(2, '0')}
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink">
            {column.short}
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-wide text-ink-tertiary">{column.hint}</span>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2.5 sm:p-3 max-h-[min(62vh,640px)]">
        {children}
      </div>
    </div>
  );
}

/**
 * Full-width calendar-style workflow board, organized by status columns.
 */
const WorkspaceBoard = ({
  items = [],
  onMove,
  onDelete,
  onWhatsApp,
  onRunAgent,
  canWhatsapp = false,
  canAgents = false,
  isMoving = false,
  typeFilter = null,
  statusFilter = null,
  boardDateLabel = null,
}) => {
  const [activeId, setActiveId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const filtered = useMemo(() => {
    let list = items;
    if (typeFilter === 'schedule') {
      list = list.filter((i) => i.type === 'schedule' || i.type === 'reminder');
    } else if (typeFilter) {
      list = list.filter((i) => i.type === typeFilter);
    }
    if (statusFilter) {
      list = list.filter((i) => i.status === statusFilter);
    }
    return list;
  }, [items, typeFilter, statusFilter]);

  const columns = useMemo(() => {
    return WORKSPACE_COLUMNS.map((col) => ({
      ...col,
      items: filtered
        .filter((i) => i.status === col.status)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    }));
  }, [filtered]);

  const itemMap = useMemo(() => {
    const map = {};
    filtered.forEach((item) => {
      map[item.id] = item;
    });
    return map;
  }, [filtered]);

  const activeItem = activeId ? itemMap[activeId] : null;

  const findColumnOf = (id) => {
    if (String(id).startsWith('col-')) return String(id).replace(/^col-/, '');
    return itemMap[id]?.status || null;
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverCol(null);
    if (!over || !onMove) return;

    const id = active.id;
    const from = findColumnOf(id);
    let to = findColumnOf(over.id);
    if (!to && String(over.id).startsWith('col-')) {
      to = String(over.id).replace(/^col-/, '');
    }
    if (!from || !to) return;

    const targetColItems = columns.find((c) => c.status === to)?.items || [];
    const without = targetColItems.filter((i) => i.id !== id);
    const overIndex = without.findIndex((i) => i.id === over.id);
    const nextList = [...without];
    if (overIndex >= 0) nextList.splice(overIndex, 0, itemMap[id]);
    else nextList.push(itemMap[id]);

    const orderedIds = nextList.map((i) => i.id);
    if (from === to && orderedIds.join() === targetColItems.map((i) => i.id).join()) return;

    onMove({
      id,
      status: to,
      ordered_ids: orderedIds,
      sort_order: Math.max(0, orderedIds.indexOf(id)),
    });
  };

  const todayLabel =
    boardDateLabel ||
    new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const visibleColumns = statusFilter
    ? columns.filter((c) => c.status === statusFilter)
    : columns;

  return (
    <div className="relative w-full overflow-hidden rounded-md border border-ink bg-surface shadow-sm">
      {isMoving ? (
        <div className="absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-xs text-ink shadow-sm">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Updating…
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink bg-ink px-4 py-3 sm:px-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-on-dark">
            Workflow board
          </p>
          <p className="mt-0.5 text-[11px] text-ink-on-dark-muted">
            Organized by state · drag cards across columns
          </p>
        </div>
        <p className="text-sm font-medium text-ink-on-dark">{todayLabel}</p>
      </div>

      {!statusFilter && (
        <div className="grid grid-cols-3 border-b border-ink bg-ink">
          {WORKSPACE_COLUMNS.map((col) => (
            <div
              key={col.status}
              className="border-r border-white/15 px-3 py-2.5 text-center last:border-r-0"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-on-dark">
                {col.short}
              </p>
            </div>
          ))}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={(e) => setActiveId(e.active.id)}
        onDragOver={(e) => {
          const overId = e.over?.id;
          if (!overId) {
            setOverCol(null);
            return;
          }
          setOverCol(findColumnOf(overId));
        }}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setActiveId(null);
          setOverCol(null);
        }}
      >
        <div
          className={`grid min-h-[480px] ${
            statusFilter ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'
          }`}
        >
          {visibleColumns.map((column) => {
            const ids = column.items.map((i) => i.id);
            return (
              <BoardColumn
                key={column.status}
                column={column}
                count={column.items.length}
                isOver={overCol === column.status}
                dimmed={Boolean(statusFilter && statusFilter !== column.status)}
              >
                <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                  {column.items.length === 0 ? (
                    <div className="flex flex-1 items-center justify-center px-3 py-16 text-center">
                      <p className="text-xs text-ink-tertiary">
                        {filtered.length === 0 ? 'Nothing in this view yet' : 'Drop cards here'}
                      </p>
                    </div>
                  ) : (
                    column.items.map((item) => (
                      <SortableCard
                        key={item.id}
                        item={item}
                        onDelete={onDelete}
                        onWhatsApp={onWhatsApp}
                        onRunAgent={onRunAgent}
                        canWhatsapp={canWhatsapp}
                        canAgents={canAgents}
                      />
                    ))
                  )}
                </SortableContext>
              </BoardColumn>
            );
          })}
        </div>
        <DragOverlay>
          {activeItem ? (
            <WorkspaceCard
              item={activeItem}
              dragging
              canWhatsapp={canWhatsapp}
              canAgents={canAgents}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <div className="flex flex-wrap items-center gap-4 border-t border-border bg-surface-muted/60 px-4 py-2 text-[10px] text-ink-tertiary sm:px-5">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded-sm bg-ink" />
          Task / meeting
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded-sm bg-ink-secondary" />
          Schedule
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-4 rounded-sm border border-border bg-surface" />
          Done
        </span>
        <span className="ml-auto hidden sm:inline">Full-width · filter by type or state above</span>
      </div>
    </div>
  );
};

export default WorkspaceBoard;
